import { MIN_SEARCH_RADIUS_M } from '@/features/place/constants/place-constants';
import {
  type Coordinates,
  type PlaceBoundingBox,
} from '@/features/place/types/place-types';
import calculateDegrees from '@/features/place/utils/calculate-degrees';
import calculateEarthRadius from '@/features/place/utils/calculate-earth-radius';
import calculateRadians from '@/features/place/utils/calculate-radians';

export default function calculateBboxBasedOnCenterAndRadius(coordinates: Coordinates): PlaceBoundingBox {
  const {
    latitude,
    longitude,
  } = coordinates;

  const bearing = 45;

  //  expanding the bounding box to the desired size
  const earthRadius = calculateEarthRadius(latitude); // radius Earth in meters
  const longitudeRadians = calculateRadians(longitude);
  const latitudeRadians = calculateRadians(latitude);
  const bearingRads = calculateRadians(bearing);
  const maxLatitudeRadians = Math.asin(
    Math.sin(latitudeRadians)
      * Math.cos(MIN_SEARCH_RADIUS_M / earthRadius)
      + Math.cos(latitudeRadians)
      * Math.sin(MIN_SEARCH_RADIUS_M / earthRadius)
      * Math.cos(bearingRads),
  );
  const maxLongitudeRadians = longitudeRadians + Math.atan2(
    (Math.sin(bearingRads) * Math.sin(MIN_SEARCH_RADIUS_M / earthRadius) * Math.cos(latitudeRadians)),
    (Math.cos(MIN_SEARCH_RADIUS_M / earthRadius) - Math.sin(latitudeRadians) * Math.sin(maxLatitudeRadians)),
  );
  const minLatitudeRadians = Math.asin(
    Math.sin(latitudeRadians)
      * Math.cos(MIN_SEARCH_RADIUS_M / earthRadius)
      - Math.cos(latitudeRadians)
      * Math.sin(MIN_SEARCH_RADIUS_M / earthRadius)
      * Math.cos(bearingRads),
  );
  const minLongitudeRadians = longitudeRadians - Math.atan2(
    (Math.sin(bearingRads) * Math.sin(MIN_SEARCH_RADIUS_M / earthRadius) * Math.cos(latitudeRadians)),
    (Math.cos(MIN_SEARCH_RADIUS_M / earthRadius) - Math.sin(latitudeRadians) * Math.sin(maxLatitudeRadians)),
  );

  return {
    latitudeNorthEast: calculateDegrees(maxLatitudeRadians),
    latitudeSouthWest: calculateDegrees(minLatitudeRadians),
    longitudeNorthEast: calculateDegrees(maxLongitudeRadians),
    longitudeSouthWest: calculateDegrees(minLongitudeRadians),
  };
}
